import { useState, useEffect } from 'react';
import { AccountInfo, InteractionRequiredAuthError } from '@azure/msal-browser';
import { msalInstance, loginRequest } from '../config/auth';
import type { UserHeaders } from '../services/api';

interface AuthState {
  account: AccountInfo | null;
  token: string | null;
  user: UserHeaders | null;
  loading: boolean;
  error: string | null;
}

export function useAuth() {
  const [state, setState] = useState<AuthState>({
    account: null,
    token: null,
    user: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    msalInstance.initialize().then(async () => {
      // Required: process the auth response if this window is the popup redirect target
      await msalInstance.handleRedirectPromise();
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        acquireToken(accounts[0]);
      } else {
        setState(s => ({ ...s, loading: false }));
      }
    });
  }, []);

  const acquireToken = async (account: AccountInfo) => {
    try {
      const response = await msalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      const claims = account.idTokenClaims as Record<string, unknown> | undefined;
      const roles = Array.isArray(claims?.roles) ? (claims.roles as string[]) : [];
      const user: UserHeaders = {
        userId: account.localAccountId,
        upn: account.username,
        name: account.name ?? account.username,
        roles,
      };
      setState({ account, token: response.accessToken, user, loading: false, error: null });
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Silent failed — try popup to get a fresh token
        try {
          const popup = await msalInstance.acquireTokenPopup({ ...loginRequest, account });
          const claims = account.idTokenClaims as Record<string, unknown> | undefined;
          const roles = Array.isArray(claims?.roles) ? (claims.roles as string[]) : [];
          const user: UserHeaders = {
            userId: account.localAccountId,
            upn: account.username,
            name: account.name ?? account.username,
            roles,
          };
          setState({ account, token: popup.accessToken, user, loading: false, error: null });
        } catch {
          // Popup also failed — clear cached account so the user sees the login button
          msalInstance.clearCache();
          setState({ account: null, token: null, user: null, loading: false, error: null });
        }
      } else {
        setState(s => ({ ...s, loading: false, error: 'Error al obtener token' }));
      }
    }
  };

  const login = async () => {
    setState(s => ({ ...s, loading: true, error: null }));
    try {
      // Popup is required inside Office.js iframes (no redirect allowed)
      const response = await msalInstance.loginPopup(loginRequest);
      await acquireToken(response.account);
    } catch {
      setState(s => ({ ...s, loading: false, error: 'Inicio de sesión cancelado o fallido' }));
    }
  };

  const logout = () => {
    msalInstance.logoutPopup();
    setState({ account: null, token: null, user: null, loading: false, error: null });
  };

  return { ...state, login, logout };
}
